import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IGetDishInfoV2, IItemForGetShortDishInfo } from 'types';
import { additionalModalInitialState, AdditionalModalContext } from './additionalModalContext';
import { additionalModalReducer } from './additionalModalReducer';
import { AdditionalModalActionTypes } from './additionalModalActions';
import { useFetchData } from 'hooks/useFetchData';
import { getDishInfoV2 } from 'backend/dishes';

export const AdditionalModalState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(additionalModalReducer, additionalModalInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const dishID = params.get('dishID');

	const { response, loading } = useFetchData<IGetDishInfoV2, string>(dishID ? getDishInfoV2 : null, dishID || '');

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: AdditionalModalActionTypes.SET_ALL_DATA,
				payload: {
					dishInfo: response.data,
					IDsSelectedRestaurant: response.data.restaurants.map((restaurant) => restaurant.id),
					fetchedAdditionalDishes: response.data.additionalDishes.map((additionalDish) => ({
						...additionalDish,
						externalId: null,
					})),
					selectedAdditionalDishes: response.data.additionalDishes.map((additionalDish) => additionalDish.id),
				},
			});
		}
	}, [response]);

	const setAdditionalDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: AdditionalModalActionTypes.SET_ADDITIONAL_DISH_CATEGORY,
			payload: {
				selectedAdditionalDishCategory: value,
			},
		});
	}, []);

	const setAdditionalDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: AdditionalModalActionTypes.SET_ADDITIONAL_DISH_SUB_CATEGORY,
			payload: {
				selectedAdditionalDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedAdditionalDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedAdditionalDishes.includes(el.id));
			const filteredFetchedAdditionalDishes = state.fetchedAdditionalDishes.filter((el) =>
				state.selectedAdditionalDishes.includes(el.id),
			);

			dispatch({
				type: AdditionalModalActionTypes.SET_FETCHED_ADDITIONAL_DISHES,
				payload: {
					fetchedAdditionalDishes: [...filteredValue, ...filteredFetchedAdditionalDishes],
				},
			});
		},
		[state.selectedAdditionalDishes, state.fetchedAdditionalDishes],
	);

	const setAdditionalDishes = useCallback((value: string[]) => {
		dispatch({
			type: AdditionalModalActionTypes.SET_ADDITIONAL_DISHES,
			payload: {
				selectedAdditionalDishes: value,
			},
		});
	}, []);

	const removeAdditionalDish = useCallback(
		(value: string) => {
			const updatedSelectedAdditionalDishes = state.selectedAdditionalDishes.filter((el) => el !== value);

			dispatch({
				type: AdditionalModalActionTypes.SET_ADDITIONAL_DISHES,
				payload: {
					selectedAdditionalDishes: updatedSelectedAdditionalDishes,
				},
			});
		},
		[state.selectedAdditionalDishes],
	);

	return (
		<AdditionalModalContext.Provider
			value={{
				setAdditionalDishCategory,
				setAdditionalDishSubCategory,
				setFetchedAdditionalDishes,
				setAdditionalDishes,
				removeAdditionalDish,
				dishInfo: state.dishInfo,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				selectedAdditionalDishCategory: state.selectedAdditionalDishCategory,
				selectedAdditionalDishSubCategory: state.selectedAdditionalDishSubCategory,
				fetchedAdditionalDishes: state.fetchedAdditionalDishes,
				selectedAdditionalDishes: state.selectedAdditionalDishes,
			}}>
			{children}
		</AdditionalModalContext.Provider>
	);
};
